/**
 * FNA Dashboard - List of clients who have completed Financial Need Analysis
 * Allows viewing reports for each client.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentAuth } from '../../../contexts/AgentAuthContext';
import { listFnaReports, deleteFnaClient } from '../../../lib/agents/fnaStorage';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { FileText, Plus, Eye, Loader2, Users, Pencil, Trash2 } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import './fna-portal.css';

export default function FNADashboard() {
  const { user, agentProfile } = useAgentAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Use Firebase Auth uid (same as stored in documents) - agentProfile.uid when available, else user.uid
  const userId = agentProfile?.uid || user?.uid;

  const loadReports = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const list = await listFnaReports({ userId, limitVal: 100 });
      setReports(list);
    } catch (err) {
      console.error('Failed to load FNA reports:', err);
      setLoadError(err.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setLoadError(null);
      return;
    }
    loadReports();
  }, [userId, loadReports]);

  const handleDelete = useCallback(async (r) => {
    if (!window.confirm(`Delete all data for ${getClientDisplayName(r)}? This cannot be undone.`)) return;
    setDeletingId(r.id);
    try {
      await deleteFnaClient(r.id);
      setReports((prev) => prev.filter((x) => x.id !== r.id));
      toast({ title: 'Client deleted', description: 'All FNA data for this client has been removed.' });
    } catch (err) {
      console.error('Delete failed:', err);
      toast({ title: 'Delete failed', description: err.message || 'Could not delete client.', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  }, [toast]);

  const getClientDisplayName = (r) => {
    const data = r.reportData || {};
    const c1 = data.client1?.name;
    const c2 = data.client2?.name;
    if (c1 && c2) return `${c1} & ${c2}`;
    return c1 || c2 || r.clientEmail || 'Client';
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="fna-app space-y-6">
      <div className="fna-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1>FNA Client Dashboard</h1>
            <p>View and manage clients who have completed Financial Need Analysis</p>
          </div>
          <Button
            className="shrink-0 shadow-md hover:shadow-lg transition-shadow"
            onClick={() => navigate('/agents/financial-need-analysis/new')}
          >
            <Plus className="w-4 h-4 mr-2" />
            New FNA
          </Button>
        </div>
      </div>

      <Card className="fna-card">
        <CardHeader className="fna-card-header">
          <CardTitle className="fna-card-title">
            <Users className="w-5 h-5" />
            Clients ({reports.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="fna-card-content pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="w-10 h-10 animate-spin mr-3 text-primary" />
              <span className="font-medium">Loading clients…</span>
            </div>
          ) : loadError ? (
            <div className="fna-empty-state">
              <FileText className="fna-empty-icon text-destructive" />
              <p className="fna-empty-title">Failed to load clients</p>
              <p className="fna-empty-desc">{loadError}</p>
              <p className="text-xs text-muted-foreground">Ensure Firestore rules allow read on fnaReports for your user.</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="fna-empty-state">
              <FileText className="fna-empty-icon" />
              <p className="fna-empty-title">No FNA reports yet</p>
              <p className="fna-empty-desc">Enter client email in Personal Info before generating a report — it is required to save.</p>
              <p className="fna-empty-desc">Create your first Financial Need Analysis to see clients here.</p>
              <Button
                className="mt-2 shadow-md"
                onClick={() => navigate('/agents/financial-need-analysis/new')}
              >
                <Plus className="w-4 h-4 mr-2" />
                New FNA
              </Button>
            </div>
          ) : (
            <div className="fna-table-wrap">
              <table className="fna-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th className="hidden sm:table-cell">Report Date</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <span className="font-semibold text-foreground">{getClientDisplayName(r)}</span>
                      </td>
                      <td className="text-muted-foreground hidden sm:table-cell">
                        {formatDate(r.createdAt)}
                      </td>
                      <td className="text-right">
                        <div className="fna-btn-group">
                          <Button
                            variant="outline"
                            size="sm"
                            className="shadow-sm"
                            onClick={() => navigate(`/agents/financial-need-analysis/report/${r.id}`)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Report
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="shadow-sm"
                            onClick={() => navigate(`/agents/financial-need-analysis/edit/${r.id}`)}
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Edit & Regenerate
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30 shadow-sm"
                            onClick={() => handleDelete(r)}
                            disabled={deletingId === r.id}
                          >
                            {deletingId === r.id ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 mr-1" />
                            )}
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
